import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

interface QueryGoaEventsPayload {
  skip?: number;
  take?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  [key: string]: any;
}

interface QueryGoaEventsResponse {
  data: any[];
  total: number;
}

const getGoasApi = async (): Promise<any[]> => {
  const response = await httpClient.get('api/goa/get', {});
  const data = response.data;
  return data?.goas ?? data?.data ?? (Array.isArray(data) ? data : []);
};

const queryGoaEventsApi = async (payload: QueryGoaEventsPayload): Promise<QueryGoaEventsResponse> => {
  const response = await httpClient.post<QueryGoaEventsResponse>('api/goa/query', payload);
  return response.data;
};

const updateGoaEventApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/goa/update', payload);
  return response.data;
};

const reorderGoasApi = async (payload: { goas: Array<{ id: string | number; position: number }> }): Promise<any> => {
  const response = await httpClient.post('api/goa/reorder', payload);
  return response.data;
};

const updateGoaSettingsApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/goa/update-settings', payload);
  return response.data;
};

const getGoaSettingsApi = async (): Promise<any> => {
  const response = await httpClient.get('api/goa/get-goa-settings', {});
  return response.data;
};

const getGoaProfitRollingApi = async (params?: any): Promise<any> => {
  const response = await httpClient.get('api/goa/profit-rolling', params || {});
  return response.data;
};

const getAllGoaProfitRollingApi = async (params?: any): Promise<any> => {
  const response = await httpClient.get('api/goa/all-profit-rolling', params || {});
  return response.data;
};

const goaImageAIApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/goa/goa-image-ai', payload);
  return response.data;
};

const bulkUpdateGoaEventsApi = async (payload: { updates: Array<{ id: number; hardIn?: number; hardOut?: number }> }): Promise<any> => {
  const response = await httpClient.post('api/goa/bulk-update', payload);
  return response.data;
};

const updateGoaAssetApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/goa/update-asset', payload);
  return response.data;
};

export const useGoas = () => {
  return useQuery({
    queryKey: ['goas'],
    queryFn: getGoasApi,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useQueryGoaEvents = () => {
  return useMutation({
    mutationFn: queryGoaEventsApi,
  });
};

export const useUpdateGoaEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGoaEventApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goa-events'] });
      queryClient.invalidateQueries({ queryKey: ['location-readings'] });
    },
  });
};

export const useReorderGoas = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderGoasApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goas'] });
      queryClient.invalidateQueries({ queryKey: ['location-details'] });
    },
  });
};

export const useUpdateGoaSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGoaSettingsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goa-settings'] });
      queryClient.invalidateQueries({ queryKey: ['goas'] });
      queryClient.invalidateQueries({ queryKey: ['location-details'] });
    },
  });
};

export const useGoaSettings = () => {
  return useQuery({
    queryKey: ['goa-settings'],
    queryFn: getGoaSettingsApi,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useGoaProfitRolling = (params?: any) => {
  return useQuery({
    queryKey: ['goa-profit-rolling', params],
    queryFn: () => getGoaProfitRollingApi(params),
    enabled: !!params,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useAllGoaProfitRolling = (params?: any) => {
  return useQuery({
    queryKey: ['all-goa-profit-rolling', params],
    queryFn: () => getAllGoaProfitRollingApi(params),
    enabled: !!params,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useGoaImageAI = () => {
  return useMutation({
    mutationFn: goaImageAIApi,
  });
};

export const useBulkUpdateGoaEvents = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkUpdateGoaEventsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goa-events'] });
      queryClient.invalidateQueries({ queryKey: ['location-readings'] });
    },
  });
};

export const useUpdateGoaAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGoaAssetApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goas'] });
      queryClient.invalidateQueries({ queryKey: ['location-details'] });
    },
  });
};
